import {
  BadgeSecondarySubtle,
  BadgeWarningSubtle,
  Icon,
  IconSVG,
  SVGColorKey,
} from "@studio3t/generic-components";
import styled from "styled-components";
import { attributesText, operationsText } from "@/pages/pipelineOperations";
import type { StageNodeData } from "./pipelineGraph";

// The badge row at the foot of a stage card: the schema the stage refs and
// the operations it takes. One row for both, since the card has no height
// for another; each badge gives way and ellipses, and its full text is in
// the tooltip.

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
`;

// What holds a badge in the row and carries its tooltip: the library's
// badges take no `title`. A slot shrinks so its badge can ellipse.
const Slot = styled.span<{ $fixed?: boolean }>`
  display: inline-flex;
  min-width: 0;
  flex: ${(p) => (p.$fixed ? "0 0 auto" : "0 1 auto")};
`;

// Schema ref badge (Figma 13984-207179): a subtle secondary badge with the
// Schema glyph and the schema's real name. Long names ellipse; the text
// colour pins to the badge token so it doesn't inherit the card link's
// green.
const SchemaBadge = styled(BadgeSecondarySubtle)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => p.theme.badgeSecondarySubtleText};
`;

const OperationsBadge = styled(SchemaBadge)``;

// The badge text itself: a flex badge cannot ellipse a bare text node, so
// the text sits in its own box that can.
const BadgeText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UnreadableBadge = styled(BadgeWarningSubtle)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  color: ${(p) => p.theme.badgeWarningSubtleColor};
`;

/** The stage's own rule. A rule that cannot be read is said to be so: it is
 *  never shown as if the stage took everything. */
function Operations({
  operations,
}: {
  operations: NonNullable<StageNodeData["operations"]>;
}) {
  if (operations.kind === "attributes") {
    return (
      <RuleBadge title="Routes" text={attributesText(operations.attributes)} />
    );
  }
  if (operations.kind !== "only") {
    return (
      <Slot
        $fixed
        title="This stage names operations that cannot be read from the pipeline code"
      >
        <UnreadableBadge>
          <IconSVG
            icon={Icon.Warning}
            size={11}
            svgColorKey={SVGColorKey.warningIcon}
          />
          Operations unreadable
        </UnreadableBadge>
      </Slot>
    );
  }
  return (
    <RuleBadge
      title="Operations"
      text={operationsText(operations.operations)}
    />
  );
}

/** A stage's readable rule: what it takes, under a neutral filter mark. */
function RuleBadge({ title, text }: { title: string; text: string }) {
  return (
    <Slot title={`${title}: ${text}`}>
      <OperationsBadge>
        <IconSVG
          icon={Icon.Filter}
          size={12}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
        <BadgeText>{text}</BadgeText>
      </OperationsBadge>
    </Slot>
  );
}

export function StageCardBadges({ data }: { data: StageNodeData }) {
  if (!data.schemaId && !data.operations) return null;
  const schema = data.schemaName ?? data.schemaId;
  return (
    <BadgeRow>
      {data.schemaId && (
        <Slot title={schema}>
          <SchemaBadge>
            <IconSVG
              icon={Icon.Schema}
              size={12}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
            <BadgeText>{schema}</BadgeText>
          </SchemaBadge>
        </Slot>
      )}
      {data.operations && <Operations operations={data.operations} />}
    </BadgeRow>
  );
}
